<template>
  <v-dialog
    v-model="dialogProxy"
    class="acceptance-receipt-dialog"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1100px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center acceptance-dialog-title">
        <span>Generating a receipt</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="acceptance-dialog-layout">
          <div :class="['acceptance-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.buyer_name"
                    label="Buyer name *"
                    outlined
                    dense
                    :error-messages="getValidationError('buyer_name')"
                    @input="clearValidationError('buyer_name')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.buyer_tax_number"
                    label="Tax number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('buyer_tax_number') }"
                    @input="clearOptionalWarning('buyer_tax_number')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.buyer_address"
                    label="Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('buyer_address')"
                    @input="clearValidationError('buyer_address')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.shipping_address"
                    label="Shipping address"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address') }"
                    @input="clearOptionalWarning('shipping_address')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.issue_place"
                    label="Issue place *"
                    outlined
                    dense
                    :error-messages="getValidationError('issue_place')"
                    @input="clearValidationError('issue_place')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="issueDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.issue_date"
                        label="Issue date *"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="getValidationError('issue_date')"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.issue_date"
                      no-title
                      scrollable
                      @input="onIssueDateSelected"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <div class="mt-6 mb-2 font-weight-bold acceptance-section-title">Products</div>

              <v-alert
                v-if="getValidationError('items').length"
                dense
                text
                type="warning"
                class="mb-3"
              >
                {{ getValidationError('items')[0] }}
              </v-alert>

              <v-row
                v-for="(item, index) in form.items"
                :key="index"
                dense
                class="mb-2"
              >
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="item.name"
                    label="Name *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'name')"
                    @input="clearItemValidationError(index, 'name')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.quantity"
                    label="Quantity *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'quantity')"
                    @input="clearItemValidationError(index, 'quantity')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.unit"
                    label="Unit"
                    outlined
                    dense
                    hide-details
                    :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'unit') }"
                    @input="clearItemOptionalWarning(index, 'unit')"
                  />
                </v-col>

                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-btn icon color="red" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mt-2" @click="addItem">
                <v-icon left>mdi-plus</v-icon>
                Add product
              </v-btn>
            </div>
          </div>

          <div v-if="showPreview" class="acceptance-preview-pane">
            <div class="acceptance-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

                <v-progress-circular
                  v-if="previewLoading"
                  indeterminate
                  size="18"
                  width="2"
                  color="primary"
                />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="acceptance-preview-frame"
              frameborder="0"
            />

            <div v-else class="acceptance-preview-empty">
              No preview available.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          @click="openPreview"
        >
          {{ showPreview ? 'Hide preview' : 'Show preview' }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="generateDocument"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

export const ENDPOINT = "sales/salesOrder";
const DOCUMENT_TYPE = "delivery_acceptance_receipt";



export default {
  name: "AcceptanceReceiptDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      validationErrors: {},
      optionalWarnings: {},
      issueDateMenu: false,
      form: {
        buyer_name: "",
        buyer_address: "",
        shipping_address: "",
        buyer_tax_number: "",
        issue_place: "Zagyvarékas",
        issue_date: "",
        items: [],
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `${ENDPOINT}/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/defaults`
        );

        this.form = {
          buyer_name: data.buyer_name || "",
          buyer_address: data.buyer_address || "",
          shipping_address: data.shipping_address || "",
          buyer_tax_number: data.buyer_tax_number || "",
          issue_place: data.issue_place || "Zagyvarékas",
          issue_date: data.issue_date || "",
          items: Array.isArray(data.items) ? data.items.filter(item => !String(item.name).includes('Szállítási díj')) : [],
        };

        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    hasItemOptionalWarning(index, field) {
      return Boolean(this.optionalWarnings[this.getItemValidationKey(index, field)]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    clearItemOptionalWarning(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    getValidationError(field) {
      return this.validationErrors[field] || [];
    },

    getItemValidationKey(index, field) {
      return `items.${index}.${field}`;
    },

    getItemValidationError(index, field) {
      return this.validationErrors[this.getItemValidationKey(index, field)] || [];
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    clearItemValidationError(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.validationErrors[key]) {
        this.$delete(this.validationErrors, key);
      }
    },

    validateForm() {
      const errors = {};
      const requiredFields = [
        { key: "buyer_name", label: "Buyer name" },
        { key: "buyer_address", label: "Address" },
        { key: "issue_place", label: "Issue place" },
        { key: "issue_date", label: "Issue date" },
      ];

      requiredFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          errors[field.key] = [`${field.label} is required.`];
        }
      });

      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        errors.items = ["At least one product is required."];
      } else {
        this.form.items.forEach((item, index) => {
          [
            { key: "name", label: "Product name" },
            { key: "quantity", label: "Quantity" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              errors[this.getItemValidationKey(index, field.key)] = [
                `${field.label} is required.`,
              ];
            }
          });
        });
      }

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "buyer_tax_number", label: "Buyer Tax number" },
        { key: "shipping_address", label: "Shipping address" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          missingFields.push(field);
        }
      });

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, index) => {
          if (this.isEmpty(item.unit)) {
            missingFields.push({
              key: this.getItemValidationKey(index, "unit"),
              label: `Product ${index + 1} - Unit`,
            });
          }
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    onIssueDateSelected() {
      this.issueDateMenu = false;
      this.clearValidationError("issue_date");
    },

    addItem() {
      this.form.items.push({
        name: "",
        quantity: 1,
        unit: "db",
      });
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
      if(this.showPreview === true){
        this.showPreview = false;
      }else{
        this.showPreview = true;
      }

      await this.refreshPreview();
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `${ENDPOINT}/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/preview`,
          this.form
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinue = await this.confirmMissingOptionalFields();

      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `${ENDPOINT}/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/generate`,
          this.form
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        console.error(error);

        Swal.fire({
          title: "Error",
          icon: "error",
          text: "The document could not be created.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.acceptance-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.acceptance-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.acceptance-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.acceptance-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
}

.acceptance-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.acceptance-preview-frame {
  width: 100%;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.acceptance-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.acceptance-dialog-title {
  font-size: 26px !important;
}

.acceptance-section-title {
  font-size: 20px;
}

.acceptance-receipt-dialog ::v-deep .v-label,
.acceptance-receipt-dialog ::v-deep .v-input input,
.acceptance-receipt-dialog ::v-deep .v-input textarea,
.acceptance-receipt-dialog ::v-deep .v-select__selection,
.acceptance-receipt-dialog ::v-deep .v-text-field__slot input {
  font-size: 18px !important;
}

.acceptance-receipt-dialog ::v-deep .v-label {
  font-size: 17px !important;
}

.acceptance-receipt-dialog ::v-deep .v-btn {
  font-size: 16px !important;
}

.acceptance-receipt-dialog ::v-deep .v-btn .v-icon {
  font-size: 22px !important;
}

.acceptance-preview-toolbar {
  font-size: 18px;
}

.acceptance-preview-empty {
  font-size: 18px;
}


.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}

</style>